import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useEffect, useRef, useCallback, memo, useState } from "react";
import { FormActions } from "./FormActions";
import { useTranslation } from "react-i18next";

// Module-level so hook dependencies stay stable across renders
const tabOrder = ["basic", "details", "qualifications", "metadata"];

interface FormTabsProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  children: React.ReactNode;
  onCancel: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
  isCreateMode?: boolean;
}

export const FormTabs = memo(({
  activeTab,
  setActiveTab,
  children,
  onCancel,
  onSubmit,
  isSubmitting,
  isCreateMode = false
}: FormTabsProps) => {
  const { t } = useTranslation();

  const initialRender = useRef(true);
  const formRef = useRef<HTMLFormElement>(null);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    }
  }, []);

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value);
    setErrors([]);
  }, [setActiveTab]);

  const validateForm = useCallback(() => {
    if (!formRef.current) return true;

    const newErrors: string[] = [];

    // Check job title specifically
    const titleField = formRef.current.querySelector('input#title');
    if (titleField && !(titleField as HTMLInputElement).value.trim()) {
      newErrors.push(t("formErrors.jobTitleRequired"));
    }

    // Check company specifically
    const companyField = formRef.current.querySelector('input[name=\"companyId\"]');
    if (companyField && !(companyField as HTMLInputElement).value.trim()) {
      newErrors.push(t("formErrors.companyRequired"));
      document.querySelector('#company-selector')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // Optionally, check other required fields generically
    const requiredFields = formRef.current.querySelectorAll('input[required], textarea[required], select[required]');
    for (const field of Array.from(requiredFields)) {
      const input = field as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      // Skip job title and company, already handled
      if (
        (input.id === "title") ||
        (input.name === "companyId")
      ) continue;
      if (!input.value.trim()) {
        const label = input.closest('div')?.querySelector('label')?.textContent?.replace(' *', '') || 'Field';
        newErrors.push(`${label} is required`);
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  }, [activeTab, t]);

  const handleFormSubmit = useCallback((e: any) => {
    e.preventDefault();

    let isExplicitSave = false;
    let isSubmitButtonClick = false;

    if (e.nativeEvent?._isSubmitButtonClick) {
      isSubmitButtonClick = true;
    }

    if (e.nativeEvent?._isExplicitSave)
      isExplicitSave = true;

    if (isExplicitSave || isSubmitButtonClick) {
      if (validateForm()) {
        onSubmit(e);
      }
    }
  }, [onSubmit, validateForm]);

  const handleNext = useCallback((e: React.MouseEvent) => {
    const currentIndex = tabOrder.indexOf(activeTab);

    if (activeTab === "basic" && !validateForm()) {
      return;
    }

    if (currentIndex < tabOrder.length - 1) {
      e.preventDefault();
      setActiveTab(tabOrder[currentIndex + 1]);
      setErrors([]);
    }
  }, [activeTab, setActiveTab, validateForm, tabOrder]);

  const handlePrevious = useCallback(() => {
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabOrder[currentIndex - 1]);
      setErrors([]);
    }
  }, [activeTab, setActiveTab, tabOrder]);

  const handleSave = useCallback(() => {
    if (validateForm()) {
      const event = new CustomEvent('submit', { bubbles: true, cancelable: true }) as unknown as React.FormEvent;
      (event as any)._isExplicitSave = true;

      if (formRef.current) {
        formRef.current.dispatchEvent(event as unknown as Event);
      }
    }
  }, [validateForm]);

  const isLastTab = activeTab === tabOrder[tabOrder.length - 1];

  const contentHeight = isCreateMode ? "min-h-0" : "h-[calc(100vh-240px)]";

  return (
    <form
      ref={formRef}
      onSubmit={handleFormSubmit}
      className="space-y-4 flex flex-col h-full min-h-0 relative"
    >
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full flex-grow flex flex-col min-h-0">
        <TabsList className="grid grid-cols-4 mb-6">
          <TabsTrigger value="basic">{t("jobs.formWizard.basicInfo")}</TabsTrigger>
          <TabsTrigger value="details">{t("jobs.formWizard.jobDetails")}</TabsTrigger>
          <TabsTrigger value="qualifications">{t("jobs.formWizard.qualifications")}</TabsTrigger>
          <TabsTrigger value="metadata">{t("jobs.formWizard.additionalInfo")}</TabsTrigger>
        </TabsList>

        {errors.length > 0 && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
            <ul className="list-disc pl-5">
              {errors.map((err, i) => <li key={i}>{err}</li>)}
            </ul>
          </div>
        )}

        <div className={`flex-grow relative overflow-hidden ${contentHeight}`}>
          <ScrollArea className="h-full w-full">
            <div className="pr-4 pb-24">
              {children}
            </div>
          </ScrollArea>
        </div>
      </Tabs>

      <FormActions
        activeTab={activeTab}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onSave={handleSave}
        isLastTab={isLastTab}
        isCreateMode={isCreateMode}
      />
    </form>
  );
});

FormTabs.displayName = "FormTabs";
